/**
 * ShareTarget — unified model for all shareable Farmly entities.
 * Used by Share Studio for caption/image/UTM generation.
 */

export type ShareTargetType = 'activity' | 'producer' | 'shop' | 'product';

export interface ShareTargetBase {
  type: ShareTargetType;
  id: string;
  slug?: string | null;
  title: string;
  /** Resolved public URL for hero image (640/1280). null = use fallback gradient */
  heroImageUrl: string | null;
  producerName: string;
  producerSlug: string;
  /** Producer UUID — used for analytics (share_sessions). Optional for backward compat. */
  producerId?: string | null;
}

export interface ActivityShareTarget extends ShareTargetBase {
  type: 'activity';
  startDate?: string | null;
  endDate?: string | null;
  timingMode?: string | null;
  isFree: boolean;
  priceOre: number;
  bookingMode: string; // 'required' | 'optional' | 'none'
  venueText?: string | null;
  descriptionHtml?: string | null;
}

export interface ProducerShareTarget extends ShareTargetBase {
  type: 'producer';
  tagline?: string | null;
  placeName?: string | null;
}

export interface ShopShareTarget extends ShareTargetBase {
  type: 'shop';
  openingSnippet?: string | null;
  /** Optional place/venue name */
  placeName?: string | null;
  /** Short farm shop intro text (max ~160 chars) */
  shopIntro?: string | null;
}

export interface ProductShareTarget extends ShareTargetBase {
  type: 'product';
  priceOre: number;
  /** Optional place/venue name */
  placeName?: string | null;
  /** Optional delivery/pickup hint, e.g. "Hämtas på gården" */
  deliverySnippet?: string | null;
  /** Short excerpt from product description (max ~140 chars) */
  descriptionExcerpt?: string | null;
}

export type ShareTarget =
  | ActivityShareTarget
  | ProducerShareTarget
  | ShopShareTarget
  | ProductShareTarget;

/** Image format specs */
export type ShareFormat = 'ig-post' | 'ig-story' | 'fb';

export const SHARE_FORMAT_SPECS: Record<ShareFormat, { w: number; h: number; label: string }> = {
  'ig-post': { w: 1080, h: 1080, label: 'Instagram Post' },
  'ig-story': { w: 1080, h: 1920, label: 'Instagram Story' },
  'fb': { w: 1200, h: 630, label: 'Facebook' },
};

export type SharePlatform = 'instagram' | 'facebook' | 'bio' | 'qr';
export type TemplateKey = 't1' | 't2' | 't3';

/** Asset type: single image or 3-slide carousel */
export type ShareAssetType = 'single' | 'carousel';

/** Carousel slide role */
export type CarouselSlideRole = 'hook' | 'info' | 'cta';
